import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Minus, ArrowLeft, Search, ChevronRight,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../../../../components/SEO/SEOHead';
import SEOFooter from '../../../../../components/SEO/SEOFooter';

// Power Rankings Data
const WEEK1_RANKINGS = [
  {
    rank: 1,
    team: 'Buffalo Bills',
    record: '1-0',
    previousRank: 3,
    movement: 'up',
    movementValue: 2,
    analysis: 'Engineered an epic comeback over the Ravens, maintaining their reputation as an AFC juggernaut.',
    keyWin: 'vs Ravens',
    nextOpponent: 'Dolphins',
    spread: '-6.5',
    tier: 'elite'
  },
  {
    rank: 2,
    team: 'Green Bay Packers',
    record: '1-0',
    previousRank: 8,
    movement: 'up',
    movementValue: 6,
    analysis: 'Defense completely shut down the Lions high-octane offense, forcing 3 turnovers.',
    keyWin: 'vs Lions',
    nextOpponent: 'Bears',
    spread: '-7',
    tier: 'elite'
  },
  {
    rank: 3,
    team: 'Indianapolis Colts',
    record: '1-0',
    previousRank: 15,
    movement: 'up',
    movementValue: 12,
    analysis: "Daniel Jones looked reborn in Indy, throwing for 370 yards and 4 TDs in a statement win.",
    keyWin: 'vs Texans',
    nextOpponent: 'Titans',
    spread: '-9.5',
    tier: 'elite'
  },
  {
    rank: 4,
    team: 'Philadelphia Eagles',
    record: '1-0',
    previousRank: 4,
    movement: 'same',
    movementValue: 0,
    analysis: 'Survived a physical NFC East battle with Dallas, showing championship grit.',
    keyWin: 'vs Cowboys',
    nextOpponent: 'Chiefs',
    spread: '-3',
    tier: 'elite'
  },
  {
    rank: 5,
    team: 'Los Angeles Chargers',
    record: '1-0',
    previousRank: 12,
    movement: 'up',
    movementValue: 7,
    analysis: 'Justin Herbert outdueled Mahomes in a thrilling 27-24 victory over the Chiefs.',
    keyWin: 'vs Chiefs',
    nextOpponent: 'Raiders',
    spread: '-4.5',
    tier: 'elite'
  },
  {
    rank: 6,
    team: 'Jacksonville Jaguars',
    record: '1-0',
    previousRank: 18,
    movement: 'up',
    movementValue: 12,
    analysis: "Offensive explosion with 41 points showed what this talented roster is capable of.",
    keyWin: 'vs Dolphins',
    nextOpponent: 'Browns',
    spread: '-2.5',
    tier: 'contenders'
  },
  {
    rank: 7,
    team: 'Cincinnati Bengals',
    record: '1-0',
    previousRank: 9,
    movement: 'up',
    movementValue: 2,
    analysis: "Joe Burrow and the offense showed midseason form in a rivalry win.",
    keyWin: 'vs Browns',
    nextOpponent: 'Ravens',
    spread: 'PK',
    tier: 'contenders'
  },
  {
    rank: 8,
    team: 'San Francisco 49ers',
    record: '1-0',
    previousRank: 2,
    movement: 'down',
    movementValue: -6,
    analysis: 'Ground game dominated with 189 rushing yards, but passing game looked out of sync.',
    keyWin: '@ Seahawks',
    nextOpponent: 'Rams',
    spread: '-7.5',
    tier: 'contenders'
  },
  {
    rank: 9,
    team: 'Las Vegas Raiders',
    record: '1-0',
    previousRank: 22,
    movement: 'up',
    movementValue: 13,
    analysis: "Dominant road win in Carolina showed a complete team effort.",
    keyWin: '@ Panthers',
    nextOpponent: 'Chargers',
    spread: '+4.5',
    tier: 'contenders'
  },
  {
    rank: 10,
    team: 'Denver Broncos',
    record: '1-0',
    previousRank: 14,
    movement: 'up',
    movementValue: 4,
    analysis: 'Defense recorded 5 sacks and forced 3 turnovers in a dominant performance.',
    keyWin: 'vs Titans',
    nextOpponent: 'Steelers',
    spread: '-1',
    tier: 'contenders'
  },
  {
    rank: 11,
    team: 'Los Angeles Rams',
    record: '1-0',
    previousRank: 11,
    movement: 'same',
    movementValue: 0,
    analysis: "Matthew Stafford looked sharp with 3 TDs in a professional win.",
    keyWin: 'vs Cardinals',
    nextOpponent: '49ers',
    spread: '+7.5',
    tier: 'contenders'
  },
  {
    rank: 12,
    team: 'Chicago Bears',
    record: '0-1',
    previousRank: 16,
    movement: 'up',
    movementValue: 4,
    analysis: "Caleb Williams showed elite potential despite the loss to Green Bay.",
    keyLoss: 'vs Packers',
    nextOpponent: 'Packers',
    spread: '+7',
    tier: 'wildcard'
  },
  {
    rank: 13,
    team: 'Arizona Cardinals',
    record: '1-0',
    previousRank: 25,
    movement: 'up',
    movementValue: 12,
    analysis: 'Marvin Harrison Jr. announced his arrival with 8 catches for 147 yards.',
    keyWin: '@ Saints',
    nextOpponent: 'Giants',
    spread: '-3.5',
    tier: 'wildcard'
  },
  {
    rank: 14,
    team: 'Pittsburgh Steelers',
    record: '1-0',
    previousRank: 20,
    movement: 'up',
    movementValue: 6,
    analysis: "Defense forced 4 turnovers while the offense capitalized with 31 points.",
    keyWin: 'vs Jets',
    nextOpponent: 'Broncos',
    spread: '+1',
    tier: 'wildcard'
  },
  {
    rank: 15,
    team: 'Minnesota Vikings',
    record: '1-0',
    previousRank: 17,
    movement: 'up',
    movementValue: 2,
    analysis: 'Justin Jefferson dominated with 11 catches in a Monday Night thriller.',
    keyWin: 'vs Giants',
    nextOpponent: 'Lions',
    spread: '+3',
    tier: 'wildcard'
  },
  {
    rank: 16,
    team: 'Tampa Bay Buccaneers',
    record: '1-0',
    previousRank: 19,
    movement: 'up',
    movementValue: 3,
    analysis: 'Perfect 4-for-4 in the red zone showed offensive efficiency.',
    keyWin: '@ Falcons',
    nextOpponent: 'Saints',
    spread: '-4',
    tier: 'wildcard'
  },
  {
    rank: 17,
    team: 'New York Jets',
    record: '0-1',
    previousRank: 10,
    movement: 'down',
    movementValue: -7,
    analysis: 'Aaron Rodgers looked healthy with 300+ yards, but 4 turnovers doomed them.',
    keyLoss: '@ Steelers',
    nextOpponent: 'Patriots',
    spread: '-7',
    tier: 'wildcard'
  },
  {
    rank: 18,
    team: 'Cleveland Browns',
    record: '0-1',
    previousRank: 13,
    movement: 'down',
    movementValue: -5,
    analysis: 'Defense played well enough to win, but offensive red zone struggles continued.',
    keyLoss: 'vs Bengals',
    nextOpponent: 'Jaguars',
    spread: '+2.5',
    tier: 'wildcard'
  },
  {
    rank: 19,
    team: 'Dallas Cowboys',
    record: '0-1',
    previousRank: 6,
    movement: 'down',
    movementValue: -13,
    analysis: 'Dak Prescott played well with 3 TDs, but costly penalties sealed their fate.',
    keyLoss: '@ Eagles',
    nextOpponent: 'Jets',
    spread: '-3',
    tier: 'wildcard'
  },
  {
    rank: 20,
    team: 'Washington Commanders',
    record: '1-0',
    previousRank: 24,
    movement: 'up',
    movementValue: 4,
    analysis: 'Ground game controlled the clock with 169 rushing yards.',
    keyWin: 'vs Giants',
    nextOpponent: 'Lions',
    spread: '+9',
    tier: 'wildcard'
  },
  {
    rank: 21,
    team: 'Baltimore Ravens',
    record: '0-1',
    previousRank: 5,
    movement: 'down',
    movementValue: -16,
    analysis: "Derrick Henry rushed for 151 yards but the defense allowed 17 fourth-quarter points.",
    keyLoss: '@ Bills',
    nextOpponent: 'Bengals',
    spread: 'PK',
    tier: 'underperformers'
  },
  {
    rank: 22,
    team: 'Seattle Seahawks',
    record: '0-1',
    previousRank: 7,
    movement: 'down',
    movementValue: -15,
    analysis: 'Allowed 189 rushing yards and couldn\'t generate consistent pressure.',
    keyLoss: 'vs 49ers',
    nextOpponent: 'Patriots',
    spread: '-5.5',
    tier: 'underperformers'
  },
  {
    rank: 23,
    team: 'Detroit Lions',
    record: '0-1',
    previousRank: 1,
    movement: 'down',
    movementValue: -22,
    analysis: "High-powered offense managed just 14 points as the offensive line was dominated.",
    keyLoss: '@ Packers',
    nextOpponent: 'Commanders',
    spread: '-9',
    tier: 'underperformers'
  },
  {
    rank: 24,
    team: 'New England Patriots',
    record: '0-1',
    previousRank: 23,
    movement: 'down',
    movementValue: -1,
    analysis: 'Mac Jones struggled with just 147 passing yards.',
    keyLoss: 'vs Rams',
    nextOpponent: 'Seahawks',
    spread: '+5.5',
    tier: 'underperformers'
  },
  {
    rank: 25,
    team: 'New York Giants',
    record: '0-1',
    previousRank: 21,
    movement: 'down',
    movementValue: -4,
    analysis: "Russell Wilson looked uncomfortable in the new system.",
    keyLoss: '@ Commanders',
    nextOpponent: 'Cardinals',
    spread: '+3.5',
    tier: 'underperformers'
  },
  {
    rank: 26,
    team: 'Carolina Panthers',
    record: '0-1',
    previousRank: 26,
    movement: 'same',
    movementValue: 0,
    analysis: 'Offensive line allowed 6 sacks as the Panthers managed just 10 points.',
    keyLoss: 'vs Raiders',
    nextOpponent: 'Chargers',
    spread: '+10',
    tier: 'rebuilding'
  },
  {
    rank: 27,
    team: 'Miami Dolphins',
    record: '0-1',
    previousRank: 27,
    movement: 'same',
    movementValue: 0,
    analysis: 'Tua threw 3 interceptions as the Dolphins were blown out at home.',
    keyLoss: 'vs Jaguars',
    nextOpponent: 'Bills',
    spread: '+6.5',
    tier: 'rebuilding'
  },
  {
    rank: 28,
    team: 'Atlanta Falcons',
    record: '0-1',
    previousRank: 28,
    movement: 'same',
    movementValue: 0,
    analysis: 'Led in the fourth quarter but couldn\'t close out Tampa Bay.',
    keyLoss: 'vs Buccaneers',
    nextOpponent: 'Eagles',
    spread: '+6',
    tier: 'rebuilding'
  },
  {
    rank: 29,
    team: 'Tennessee Titans',
    record: '0-1',
    previousRank: 29,
    movement: 'same',
    movementValue: 0,
    analysis: "Will Levis was sacked 5 times and threw 2 picks in a rough debut.",
    keyLoss: '@ Broncos',
    nextOpponent: 'Colts',
    spread: '+9.5',
    tier: 'rebuilding'
  },
  {
    rank: 30,
    team: 'Houston Texans',
    record: '0-1',
    previousRank: 30,
    movement: 'same',
    movementValue: 0,
    analysis: "C.J. Stroud was under pressure all game, completing just 52% of his passes.",
    keyLoss: '@ Colts',
    nextOpponent: 'Bears',
    spread: '+2.5',
    tier: 'rebuilding'
  },
  {
    rank: 31,
    team: 'Kansas City Chiefs',
    record: '0-1',
    previousRank: 31,
    movement: 'same',
    movementValue: 0,
    analysis: 'Patrick Mahomes played well but receivers dropped 7 passes.',
    keyLoss: 'vs Chargers',
    nextOpponent: 'Eagles',
    spread: '+3',
    tier: 'rebuilding'
  },
  {
    rank: 32,
    team: 'New Orleans Saints',
    record: '0-1',
    previousRank: 32,
    movement: 'same',
    movementValue: 0,
    analysis: 'Derek Carr threw for just 142 yards as the offense looked out of sync.',
    keyLoss: 'vs Cardinals',
    nextOpponent: 'Buccaneers',
    spread: '+4',
    tier: 'rebuilding'
  }
];

const TIER_STYLES = {
  elite: 'border-l-4 border-l-emerald-500',
  contenders: 'border-l-4 border-l-blue-500',
  wildcard: 'border-l-4 border-l-purple-500',
  underperformers: 'border-l-4 border-l-orange-500',
  rebuilding: 'border-l-4 border-l-gray-500'
};

const TIER_LABELS = {
  elite: 'Elite',
  contenders: 'Contenders',
  wildcard: 'Wild Card',
  underperformers: 'Underperforming',
  rebuilding: 'Rebuilding'
};

const PowerRankings = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  // Structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "NFL Week 1 Power Rankings 2025 - PickStar AI Analysis",
    "description": "Complete NFL power rankings after Week 1 of the 2025 season. Expert AI analysis of all 32 teams with detailed breakdowns and tier rankings.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-09-10",
    "dateModified": "2025-09-10",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/resources/nfl/2025/week1/power-rankings"
    }
  };

  const filteredRankings = WEEK1_RANKINGS.filter(team => {
    const matchesTier = selectedTier === 'all' || team.tier === selectedTier;
    const matchesSearch = team.team.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTier && matchesSearch;
  });

  const tierCounts = WEEK1_RANKINGS.reduce((acc, team) => {
    acc[team.tier] = (acc[team.tier] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <SEOHead
        title="NFL Week 1 Power Rankings 2025 | Complete Team Analysis - PickStar AI"
        description="NFL Week 1 Power Rankings for the 2025 season. Complete analysis of all 32 teams including Bills, Packers, Eagles, and more. Expert AI-powered insights and tier rankings."
        keywords="NFL power rankings, Week 1 rankings 2025, NFL teams, Buffalo Bills, Green Bay Packers, football rankings, NFL analysis, PickStar AI"
        ogImage="https://pickstarai.com/nfl-week1-rankings.jpg"
        structuredData={articleStructuredData}
      />

      <div className="min-h-screen bg-white">
        {/* Clean Header */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link
                to="/"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
              <div className="text-xs text-gray-500 font-medium">
                UPDATED: SEPTEMBER 10, 2025
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Minimal and Clean */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-12">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-gray-900 tracking-tight">
                NFL Week 1 Power Rankings
              </h1>
              <p className="mt-2 md:mt-4 text-sm sm:text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-2">
                Complete analysis of all 32 teams after Week 1 of the 2025 season. 
                <span className="hidden sm:inline">Rankings powered by PickStar AI's advanced analytics.</span>
              </p>
            </div>

            {/* Search Bar - Clean Design */}
            <div className="mt-4 md:mt-8 max-w-md mx-auto px-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 md:w-5 h-4 md:h-5" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search teams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 md:py-3 border border-gray-300 rounded-lg text-sm md:text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
          {/* Tier Filter - Clean Pills with horizontal scroll on mobile */}
          <div className="mb-4 md:mb-8 -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide md:flex-wrap">
              <button
                onClick={() => setSelectedTier('all')}
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedTier === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Teams ({WEEK1_RANKINGS.length})
              </button>
              {Object.entries(TIER_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedTier(key)}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedTier === key
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label} ({tierCounts[key] || 0})
                </button>
              ))}
            </div>
          </div>

          {/* Rankings List - Clean Cards */}
          <div className="space-y-2 md:space-y-3">
            {filteredRankings.map((team, index) => (
              <motion.div
                key={team.team}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
                className={`bg-white border border-gray-200 rounded-lg p-3 md:p-6 hover:shadow-md transition-shadow ${TIER_STYLES[team.tier]}`}
              >
                {/* Mobile Layout */}
                <div className="md:hidden">
                  <div className="flex items-start gap-3">
                    {/* Rank */}
                    <div className="flex flex-col items-center min-w-[45px]">
                      <span className="text-2xl font-bold text-gray-900">
                        {team.rank}
                      </span>
                      {team.movementValue !== 0 && (
                        <div className="flex items-center">
                          {team.movement === 'up' ? (
                            <ArrowUp className="w-3 h-3 text-green-600" />
                          ) : team.movement === 'down' ? (
                            <ArrowDown className="w-3 h-3 text-red-600" />
                          ) : null}
                          <span className={`text-xs font-medium ${
                            team.movement === 'up' ? 'text-green-600' : 
                            team.movement === 'down' ? 'text-red-600' : 
                            'text-gray-500'
                          }`}>
                            {Math.abs(team.movementValue)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Team Info - Mobile */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {team.team}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {team.record}
                        </span>
                        {team.keyWin && (
                          <span className="text-xs text-green-600 font-medium">
                            W: {team.keyWin}
                          </span>
                        )}
                        {team.keyLoss && (
                          <span className="text-xs text-red-600 font-medium">
                            L: {team.keyLoss}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Previous Rank - Mobile */}
                    <div className="text-center min-w-[35px]">
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider">Prev</div>
                      <div className="text-sm font-medium text-gray-500">
                        {team.previousRank}
                      </div>
                    </div>
                  </div>

                  {/* Analysis and Next Game - Below on Mobile */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-600 mb-2">
                      {team.analysis}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <span>Next:</span>
                        <span className="font-medium text-gray-700">{team.nextOpponent}</span>
                      </div>
                      {team.spread && (
                        <div className="flex items-center gap-1">
                          <span>Spread:</span>
                          <span className="font-medium text-gray-700">{team.spread}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop Layout (unchanged) */}
                <div className="hidden md:flex items-start gap-6">
                  {/* Rank */}
                  <div className="flex flex-col items-center min-w-[60px]">
                    <span className="text-3xl font-bold text-gray-900">
                      {team.rank}
                    </span>
                    {team.movementValue !== 0 && (
                      <div className="flex items-center mt-1">
                        {team.movement === 'up' ? (
                          <ArrowUp className="w-4 h-4 text-green-600" />
                        ) : team.movement === 'down' ? (
                          <ArrowDown className="w-4 h-4 text-red-600" />
                        ) : null}
                        <span className={`text-sm font-medium ${
                          team.movement === 'up' ? 'text-green-600' : 
                          team.movement === 'down' ? 'text-red-600' : 
                          'text-gray-500'
                        }`}>
                          {Math.abs(team.movementValue)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Team Info */}
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {team.team}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {team.record}
                      </span>
                      {team.keyWin && (
                        <span className="text-sm text-green-600 font-medium">
                          W: {team.keyWin}
                        </span>
                      )}
                      {team.keyLoss && (
                        <span className="text-sm text-red-600 font-medium">
                          L: {team.keyLoss}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3">
                      {team.analysis}
                    </p>

                    {/* Next Game - Subtle */}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <span>Next:</span>
                        <span className="font-medium text-gray-700">{team.nextOpponent}</span>
                      </div>
                      {team.spread && (
                        <div className="flex items-center gap-1">
                          <span>Spread:</span>
                          <span className="font-medium text-gray-700">{team.spread}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Previous Rank - Subtle */}
                  <div className="text-center min-w-[50px]">
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Prev</div>
                    <div className="text-lg font-medium text-gray-500">
                      {team.previousRank}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom Section - Clean CTA */}
          <div className="mt-8 md:mt-16 bg-gray-50 rounded-lg p-6 md:p-8 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
              Get Expert Betting Analysis
            </h2>
            <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 max-w-2xl mx-auto px-2">
              Join PickStar AI for in-depth analysis, live odds tracking, and AI-powered 
              betting recommendations for every NFL game.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 md:px-6 py-2 md:py-3 bg-blue-600 text-white text-sm md:text-base font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Free Trial
            </button>
          </div>
        </div>

        {/* SEO Footer */}
        <SEOFooter />
      </div>
    </>
  );
};

export default PowerRankings;